import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Alert,
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { vendedorService } from '../services/vendedor';

const VendedorUnificar: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [idPermanecer, setIdPermanecer] = useState<number>(0);
  const [idExcluir, setIdExcluir] = useState<number>(0);
  const [errors, setErrors] = useState({
    idPermanecer: '',
    idExcluir: '',
  });

  // Buscar vendedor atual
  const { data: vendedorAtual, isLoading } = useQuery({
    queryKey: ['vendedor', id],
    queryFn: () => vendedorService.getById(Number(id)),
    enabled: !!id,
  });

  // Mutation para unificar
  const unificarMutation = useMutation({
    mutationFn: () => vendedorService.unificar(idPermanecer, idExcluir),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendedores'] });
      navigate('/vendedores');
    },
    onError: (error: any) => {
      console.error('Erro ao unificar vendedores:', error);
    },
  });

  const validateForm = (): boolean => {
    const newErrors = {
      idPermanecer: '',
      idExcluir: '',
    };
    let isValid = true;

    if (!idPermanecer || idPermanecer <= 0) {
      newErrors.idPermanecer = 'Selecione o vendedor que permanecerá';
      isValid = false;
    }

    if (!idExcluir || idExcluir <= 0) {
      newErrors.idExcluir = 'Selecione o vendedor que será excluído';
      isValid = false;
    }

    if (idPermanecer === idExcluir) {
      newErrors.idPermanecer = 'Os vendedores devem ser diferentes';
      newErrors.idExcluir = 'Os vendedores devem ser diferentes';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      unificarMutation.mutate();
    }
  };

  const handleBack = () => {
    navigate('/vendedores');
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Typography>Carregando...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography 
        variant="h4" 
        component="h1" 
        gutterBottom 
        sx={{ 
          fontWeight: 'bold',
          fontFamily: 'Arial, sans-serif',
          fontSize: '2.125rem',
          lineHeight: 1.235
        }}
      >
        Unificar Vendedor
      </Typography>
      
      <Paper sx={{ p: 3, mb: 3 }}>
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            Vendedor atual: <strong>{vendedorAtual?.nome}</strong> (ID: {vendedorAtual?.id})
          </Typography>
        </Alert>

        <Box component="form" onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              Dados da Unificação
            </Typography>
            
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
              <TextField
                fullWidth
                label="ID do Vendedor que Permanecerá"
                value={idPermanecer}
                onChange={(e) => setIdPermanecer(Number(e.target.value))}
                error={!!errors.idPermanecer}
                helperText={errors.idPermanecer}
                type="number"
                slotProps={{
                  htmlInput: { min: 1 }
                }}
                required
              />
              
              <TextField
                fullWidth
                label="ID do Vendedor que será Excluído"
                value={idExcluir}
                onChange={(e) => setIdExcluir(Number(e.target.value))}
                error={!!errors.idExcluir}
                helperText={errors.idExcluir}
                type="number"
                slotProps={{
                  htmlInput: { min: 1 }
                }}
                required
              />
            </Box>
            
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Atenção:</strong> Esta ação irá transferir todos os dados do vendedor 
                que será excluído para o vendedor que permanecerá. Esta operação não pode ser desfeita.
              </Typography>
            </Alert>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 3 }}>
            <Button
              type="submit"
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={unificarMutation.isPending}
              sx={{ minWidth: 120 }}
            >
              {unificarMutation.isPending ? 'Unificando...' : 'Unificar'}
            </Button>
            
            <Button
              type="button"
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              sx={{ minWidth: 120 }}
            >
              Voltar
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default VendedorUnificar;
